import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';

import { type VariantProps, cva } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/ui/cn';

const buttonVariants = cva(
  'relative inline-flex items-center justify-center gap-2 whitespace-nowrap border font-semibold tracking-[0.01em] transition-[color,background-color,border-color,box-shadow,transform] duration-[var(--motion-fast)] ease-[var(--bl-ease)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-border-focus))] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--bl-canvas)] active:translate-y-px disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'border-transparent bg-[linear-gradient(180deg,rgb(var(--color-primary-hover)),rgb(var(--color-primary)))] text-[rgb(var(--color-primary-contrast))] shadow-[inset_0_1px_0_rgb(var(--color-primary-light)/0.55),0_8px_22px_-12px_var(--bl-glow)] hover:shadow-[inset_0_1px_0_rgb(var(--color-primary-light)/0.7),0_10px_30px_-10px_var(--bl-glow)] hover:brightness-[1.04]',
        secondary: 'border-[color:var(--bl-line-strong)] bg-[color:var(--bl-panel)] text-[color:var(--bl-ink)] hover:border-[rgb(var(--color-primary)/0.5)] hover:bg-[color:var(--bl-gold-wash)]',
        ghost: 'border-transparent bg-transparent text-[color:var(--bl-ink-2)] hover:bg-[color:var(--bl-gold-wash)] hover:text-[color:var(--bl-ink)]',
        outline: 'border-[rgb(var(--color-primary)/0.55)] bg-transparent text-[color:var(--bl-gold-text)] hover:bg-[color:var(--bl-gold-wash)] hover:border-[rgb(var(--color-primary))]',
        error: 'border-transparent bg-[rgb(var(--color-error))] text-[rgb(var(--color-text-inverse))] shadow-[0_8px_22px_-12px_rgb(var(--color-error)/0.7)] hover:bg-[rgb(var(--color-error-hover))]',
      },
      size: {
        xs: 'h-7 rounded-[var(--radius-sm)] px-2.5 text-xs',
        sm: 'h-8 rounded-[var(--radius-sm)] px-3 text-xs',
        md: 'h-9 rounded-[var(--radius-md)] px-4 text-sm',
        lg: 'h-11 rounded-[var(--radius-md)] px-5 text-sm',
        xl: 'h-12 rounded-[var(--radius-md)] px-6 text-base',
        icon: 'h-9 w-9 rounded-[var(--radius-md)] p-0',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      loading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type={props.type ?? 'button'}
        className={cn(buttonVariants({ variant, size, fullWidth }), className)}
        disabled={disabled ?? loading}
        {...props}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : leftIcon}
        {children}
        {!loading ? rightIcon : null}
      </button>
    );
  }
);

Button.displayName = 'Button';
